import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ExternalLink, RotateCcw, RefreshCw, Save } from 'lucide-react'
import { api } from '../lib/api'

// drinx.jo URL the preview iframe loads from. Override via VITE_SITE_URL
// for local dev (e.g. http://localhost:3000). The site middleware allows
// admin.drinx.jo and localhost:5173 as frame-ancestors when ?preview=1.
const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://drinx.jo'

// Marketing Site CMS editor.
//
// Worker GET /admin/site-content returns the schema (fields + sections)
// plus the current EN/AR overrides. Each section renders as a
// collapsible card; inside is a row per field with side-by-side EN | AR
// inputs and a character counter. Saving submits only the section's
// changed fields. An empty value reverts that field to the code default
// (the worker deletes the row).
//
// Defensive: marketing role can edit, ops/accounting cannot (server
// enforces too — sidebar entry is already role-gated in Layout.tsx).

type PageId = 'home' | 'ambassadors'

type Field = {
  key: string
  page?: PageId
  section: string
  label: string
  hint?: string
  multiline?: boolean
  maxLength: number
  defaultEn: string
  defaultAr: string
}

type OverrideRow = {
  en: string
  ar: string
  updated_at?: string
  updated_by?: string | null
}

type CmsResponse = {
  pages: Array<{ id: PageId; label: string }>
  sections: string[]
  fields: Field[]
  overrides: Record<string, OverrideRow>
}

// One row of edits, indexed by `${key}:${locale}`.
type DirtyMap = Record<string, string>

export default function MarketingSite() {
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery<CmsResponse>({
    queryKey: ['site-content'],
    queryFn: () => api.get('/site-content').then(r => r.data),
  })

  const [dirty, setDirty] = useState<DirtyMap>({})
  const [savingSection, setSavingSection] = useState<string | null>(null)
  const [savedSection, setSavedSection] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [activePage, setActivePage] = useState<PageId>('home')
  // Live preview pane state. previewLocale is independent from the
  // form's editing locale — the marketer might want to edit AR copy
  // while previewing EN, or vice versa.
  //
  // Iframe always renders at mobile width (390 px). A "desktop"
  // toggle was misleading because the 640 px pane can't fit the
  // site's desktop breakpoint anyway — both modes looked mobile.
  // The "Open desktop preview" button opens the same URL in a new
  // tab where the marketer gets the actual full-window width.
  const [showPreview, setShowPreview] = useState(true)
  const [previewLocale, setPreviewLocale] = useState<'en' | 'ar'>('en')

  // Map (page, section) → fields. Fields without an explicit page
  // default to 'home' to keep older CMS entries showing under the
  // primary tab.
  const fieldsByPageAndSection = useMemo(() => {
    const out: Record<PageId, Record<string, Field[]>> = { home: {}, ambassadors: {} }
    if (!data) return out
    for (const f of data.fields) {
      const p: PageId = f.page ?? 'home'
      out[p][f.section] ??= []
      out[p][f.section].push(f)
    }
    return out
  }, [data])

  const activeSections = useMemo(() => {
    if (!data) return []
    const sectionsOnPage = new Set<string>()
    for (const f of data.fields) {
      if ((f.page ?? 'home') === activePage) sectionsOnPage.add(f.section)
    }
    return data.sections.filter(s => sectionsOnPage.has(s))
  }, [data, activePage])

  const fieldsBySection = fieldsByPageAndSection[activePage]

  // Build the dirty-overrides map filtered to the previewed locale,
  // base64-encode it for the iframe URL. Saved overrides are already
  // applied server-side by the site's getEnrichedDictionary; we only
  // ship the unsaved deltas in the URL.
  const encodedPreviewOverrides = useMemo(() => {
    const subset: Record<string, string> = {}
    for (const [dirtyKey, value] of Object.entries(dirty)) {
      const [key, locale] = dirtyKey.split(':')
      if (locale !== previewLocale) continue
      // Treat "user typed the default" as cleared (matches save logic
      // in saveSection below) so the preview shows the post-save state.
      const def = data?.fields.find(f => f.key === key)
      const effectiveDefault = locale === 'en' ? def?.defaultEn : def?.defaultAr
      subset[key] = value === effectiveDefault ? '' : value
    }
    if (Object.keys(subset).length === 0) return ''
    return btoa(JSON.stringify(subset))
  }, [dirty, previewLocale, data])

  // Debounce the encoded payload so we don't slam the iframe with a
  // reload on every keystroke. 700ms is short enough that the marketer
  // sees their edit before they move to the next field, and long enough
  // that a fast typer doesn't trigger 5 reloads in a row.
  const debouncedOverrides = useDebouncedValue(encodedPreviewOverrides, 700)

  const previewPageRoute = activePage === 'ambassadors' ? '/ambassadors' : ''
  const previewIframeUrl = `${SITE_URL}/${previewLocale}${previewPageRoute}?preview=1${
    debouncedOverrides ? `&overrides=${encodeURIComponent(debouncedOverrides)}` : ''
  }`

  function fieldDefault(key: string, locale: 'en' | 'ar'): string {
    const f = data?.fields.find(x => x.key === key)
    if (!f) return ''
    return locale === 'en' ? f.defaultEn : f.defaultAr
  }

  // The value shown in the input. Order of precedence:
  //   1. Local dirty edit (user is typing)
  //   2. Override from the DB
  //   3. Code default — so the editor always shows real text the user
  //      can read + tweak, instead of an empty input + a placeholder.
  function currentValue(key: string, locale: 'en' | 'ar'): string {
    const dirtyKey = `${key}:${locale}`
    if (dirtyKey in dirty) return dirty[dirtyKey]
    const override = data?.overrides[key]?.[locale]
    if (override) return override
    return fieldDefault(key, locale)
  }

  // Does the DB hold an active override for this (key, locale)? Used to
  // render the "Overridden" pill next to the label.
  function hasOverride(key: string, locale: 'en' | 'ar'): boolean {
    return Boolean(data?.overrides[key]?.[locale])
  }

  function setValue(key: string, locale: 'en' | 'ar', value: string) {
    setDirty(prev => ({ ...prev, [`${key}:${locale}`]: value }))
    setSaveError(null)
  }

  function resetField(key: string, locale: 'en' | 'ar') {
    // "Revert to default" populates the input with the default text and
    // marks the cell dirty so the user can see what will be saved. The
    // save logic below treats value === default as "delete the row".
    setValue(key, locale, fieldDefault(key, locale))
  }

  function discardSection(section: string) {
    if (!data) return
    const keys = (fieldsBySection[section] ?? []).map(f => f.key)
    setDirty(prev => {
      const next = { ...prev }
      for (const k of keys) {
        delete next[`${k}:en`]
        delete next[`${k}:ar`]
      }
      return next
    })
  }

  async function saveSection(section: string) {
    if (!data) return
    const keys = (fieldsBySection[section] ?? []).map(f => f.key)
    const updates: Array<{ key: string; locale: 'en' | 'ar'; value: string }> = []
    for (const k of keys) {
      for (const locale of ['en', 'ar'] as const) {
        const dirtyKey = `${k}:${locale}`
        if (dirtyKey in dirty) {
          // If the user typed the default verbatim, treat as "clear" so
          // the worker drops the row instead of storing a phantom
          // override that matches the dictionary value.
          const value = dirty[dirtyKey] === fieldDefault(k, locale) ? '' : dirty[dirtyKey]
          updates.push({ key: k, locale, value })
        }
      }
    }
    if (updates.length === 0) return

    setSavingSection(section)
    setSaveError(null)
    try {
      await api.patch('/site-content', { updates })
      // Clear the dirty entries we just saved + refetch.
      setDirty(prev => {
        const next = { ...prev }
        for (const u of updates) delete next[`${u.key}:${u.locale}`]
        return next
      })
      await qc.invalidateQueries({ queryKey: ['site-content'] })
      setSavedSection(section)
      setTimeout(() => setSavedSection(prev => (prev === section ? null : prev)), 2000)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string }
      setSaveError(axiosErr?.response?.data?.message ?? axiosErr?.message ?? 'Save failed')
    } finally {
      setSavingSection(null)
    }
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-400">Loading…</div>
  }
  if (error || !data) {
    return <div className="p-6 text-sm text-red-400">Couldn't load site content.</div>
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white">Marketing Site</h1>
          <p className="text-xs text-gray-500 mt-1">
            Edit the copy that appears on drinx.jo. Empty a field to revert to the code default.
            Changes propagate within an hour.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowPreview(v => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-2 rounded-lg border border-[#2e2e2e] hover:border-[#3a3a3a]"
        >
          <ExternalLink size={13} /> {showPreview ? 'Hide preview' : 'Show preview'}
        </button>
      </div>

      {saveError && (
        <div className="mb-4 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {saveError}
        </div>
      )}

      <div className="flex gap-1 mb-5 p-1 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg w-fit">
        {data.pages.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActivePage(p.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activePage === p.id
                ? 'bg-white text-[#1a1a1a]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={showPreview ? 'flex gap-5 items-start' : ''}>
        <div className={showPreview ? 'flex-1 min-w-0 space-y-3' : 'space-y-3 max-w-5xl'}>
          {activeSections.map(section => (
            <SectionCard
              key={section}
              section={section}
              fields={fieldsBySection[section] ?? []}
              currentValue={currentValue}
              hasOverride={hasOverride}
              setValue={setValue}
              resetField={resetField}
              discardSection={discardSection}
              saveSection={saveSection}
              saving={savingSection === section}
              saved={savedSection === section}
              dirtyCount={(fieldsBySection[section] ?? []).reduce((acc, f) => {
                return acc + (`${f.key}:en` in dirty ? 1 : 0) + (`${f.key}:ar` in dirty ? 1 : 0)
              }, 0)}
            />
          ))}
        </div>

        {showPreview && (
          <PreviewPane
            iframeUrl={previewIframeUrl}
            previewLocale={previewLocale}
            onLocaleChange={setPreviewLocale}
            pageLabel={data.pages.find(p => p.id === activePage)?.label ?? activePage}
            dirtyCount={Object.keys(dirty).length}
          />
        )}
      </div>
    </div>
  )
}

// Live preview iframe panel. Sticky-positioned next to the editor so
// the marketer can see drinx.jo update as they type. URL changes drive
// the iframe reload; React diff-checks the src attribute and only
// re-fetches on actual change. Debouncing happens in the parent.
function PreviewPane({
  iframeUrl,
  previewLocale,
  onLocaleChange,
  pageLabel,
  dirtyCount,
}: {
  iframeUrl: string
  previewLocale: 'en' | 'ar'
  onLocaleChange: (l: 'en' | 'ar') => void
  pageLabel: string
  dirtyCount: number
}) {
  const [reloadKey, setReloadKey] = useState(0)
  return (
    <div className="w-[640px] shrink-0 sticky top-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#2e2e2e] gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-500 min-w-0">
            <span className="font-semibold text-gray-300">{pageLabel}</span>
            <span>·</span>
            <span className="text-[10px] uppercase tracking-wider bg-white/5 text-gray-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              Mobile
            </span>
            {dirtyCount > 0 && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {dirtyCount} dirty
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Locale toggle — independent from form edit locale */}
            <div className="flex bg-[#242424] border border-[#3a3a3a] rounded-md overflow-hidden">
              {(['en', 'ar'] as const).map(loc => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => onLocaleChange(loc)}
                  className={`px-2 py-1 text-[11px] font-semibold ${
                    previewLocale === loc ? 'bg-white text-[#1a1a1a]' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {loc.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Open the same URL in a new tab so the marketer gets the
                site's real desktop layout at full window width. The
                iframe pane can't ever reach the site's desktop
                breakpoint (~768 px) since it's only 640 px wide. */}
            <a
              href={iframeUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this preview in a new tab (full desktop width)"
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-400 hover:text-white border border-[#3a3a3a] bg-[#242424] rounded-md"
            >
              <ExternalLink size={12} /> Desktop
            </a>
            {/* Manual refresh — useful after a save to pick up newly-persisted overrides */}
            <button
              type="button"
              onClick={() => setReloadKey(k => k + 1)}
              title="Refresh preview"
              className="px-2 py-1 text-gray-400 hover:text-white border border-[#3a3a3a] bg-[#242424] rounded-md"
            >
              <RefreshCw size={12} />
            </button>
          </div>
        </div>

        {/* Iframe container — always mobile width (390 px iPhone
            14/15 portrait). The pane isn't wide enough to honestly
            render the site's desktop breakpoint, so a "desktop" toggle
            would just be a stretched-mobile lie. Real desktop preview
            is the "Desktop" new-tab button in the toolbar. */}
        <div className="bg-[#0d0d0d] flex justify-center" style={{ height: 'calc(100vh - 200px)', minHeight: 600 }}>
          <iframe
            key={reloadKey}
            src={iframeUrl}
            title={`Preview: ${pageLabel} (${previewLocale.toUpperCase()})`}
            className="bg-white"
            style={{
              width: 390,
              height: '100%',
              border: 'none',
            }}
            // No sandbox: the iframe loads drinx.jo, our own origin,
            // and needs full JS/storage to behave normally.
          />
        </div>
      </div>
    </div>
  )
}

function SectionCard({
  section, fields, currentValue, hasOverride, setValue, resetField, discardSection, saveSection,
  saving, saved, dirtyCount,
}: {
  section: string
  fields: Field[]
  currentValue: (k: string, l: 'en' | 'ar') => string
  hasOverride: (k: string, l: 'en' | 'ar') => boolean
  setValue: (k: string, l: 'en' | 'ar', v: string) => void
  resetField: (k: string, l: 'en' | 'ar') => void
  discardSection: (s: string) => void
  saveSection: (s: string) => Promise<void>
  saving: boolean
  saved: boolean
  dirtyCount: number
}) {
  const [open, setOpen] = useState(true)

  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/2 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-white">{section}</h2>
          {dirtyCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">
              {dirtyCount} unsaved
            </span>
          )}
          {saved && (
            <span className="text-[10px] uppercase tracking-wider bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">
              Saved
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#2e2e2e] space-y-5">
          {fields.map(f => (
            <FieldRow
              key={f.key}
              field={f}
              en={currentValue(f.key, 'en')}
              ar={currentValue(f.key, 'ar')}
              enOverridden={hasOverride(f.key, 'en')}
              arOverridden={hasOverride(f.key, 'ar')}
              onEnChange={v => setValue(f.key, 'en', v)}
              onArChange={v => setValue(f.key, 'ar', v)}
              onResetEn={() => resetField(f.key, 'en')}
              onResetAr={() => resetField(f.key, 'ar')}
            />
          ))}

          {dirtyCount > 0 && (
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#2e2e2e]">
              <button
                type="button"
                onClick={() => discardSection(section)}
                disabled={saving}
                className="px-3 py-2 text-xs text-gray-400 hover:text-white"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => saveSection(section)}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg"
              >
                <Save size={13} />
                {saving ? 'Saving…' : 'Save section'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function FieldRow({
  field, en, ar, enOverridden, arOverridden,
  onEnChange, onArChange, onResetEn, onResetAr,
}: {
  field: Field
  en: string
  ar: string
  enOverridden: boolean
  arOverridden: boolean
  onEnChange: (v: string) => void
  onArChange: (v: string) => void
  onResetEn: () => void
  onResetAr: () => void
}) {
  return (
    <div className="pt-4">
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-xs font-medium text-white">{field.label}</label>
        <span className="text-[10px] text-gray-600 font-mono">{field.key}</span>
      </div>
      {field.hint && <p className="text-[11px] text-gray-500 mb-2 leading-relaxed">{field.hint}</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        <LocaleField
          locale="English"
          value={en}
          overridden={enOverridden}
          maxLength={field.maxLength}
          multiline={field.multiline}
          onChange={onEnChange}
          onReset={onResetEn}
        />
        <LocaleField
          locale="العربية"
          value={ar}
          overridden={arOverridden}
          maxLength={field.maxLength}
          multiline={field.multiline}
          rtl
          onChange={onArChange}
          onReset={onResetAr}
        />
      </div>
    </div>
  )
}

function LocaleField({
  locale, value, overridden, maxLength, multiline, rtl, onChange, onReset,
}: {
  locale: string
  value: string
  overridden: boolean
  maxLength: number
  multiline?: boolean
  rtl?: boolean
  onChange: (v: string) => void
  onReset: () => void
}) {
  const overLimit = value.length > maxLength
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">{locale}</span>
          {/* "Overridden" pill: this (key, locale) has a row in the DB.
              Absent = the field is showing the code default and saving
              with no edits is a no-op. */}
          {overridden && (
            <span className="text-[9px] uppercase tracking-wider bg-[#C41E3A]/15 text-[#C41E3A] px-1.5 py-0.5 rounded-full">
              Overridden
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-mono ${
              overLimit ? 'text-red-400' : value.length > maxLength * 0.9 ? 'text-amber-400' : 'text-gray-600'
            }`}
          >
            {value.length}/{maxLength}
          </span>
          {overridden && (
            <button
              type="button"
              onClick={onReset}
              title="Revert to default"
              className="text-gray-500 hover:text-white"
            >
              <RotateCcw size={11} />
            </button>
          )}
        </div>
      </div>
      {multiline ? (
        <textarea
          dir={rtl ? 'rtl' : 'ltr'}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          maxLength={maxLength + 50}
          className={`w-full bg-[#242424] border rounded-lg px-3 py-2 text-sm text-white focus:outline-none resize-y ${
            overLimit ? 'border-red-500' : 'border-[#3a3a3a] focus:border-[#C41E3A]'
          }`}
        />
      ) : (
        <input
          type="text"
          dir={rtl ? 'rtl' : 'ltr'}
          value={value}
          onChange={e => onChange(e.target.value)}
          maxLength={maxLength + 50}
          className={`w-full bg-[#242424] border rounded-lg px-3 py-2 text-sm text-white focus:outline-none ${
            overLimit ? 'border-red-500' : 'border-[#3a3a3a] focus:border-[#C41E3A]'
          }`}
        />
      )}
    </div>
  )
}

// Debounces a fast-changing value (typically text input). Returns the
// previous value until the input has been stable for `delayMs`. Used
// by the CMS preview iframe so we don't reload the page on every
// keystroke — only once the marketer pauses typing.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}
